import React, { useEffect, useState } from "react";
import { Loader2, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money, today } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";
import { PAYMENT_MODES } from "./config";

export const PaymentDialog = ({ doc, open, onOpenChange, onSaved }) => {
  const due = Math.max(0, (doc?.total || 0) - (doc?.paid_amount || 0));
  const [form, setForm] = useState({ amount: due, date: today(), mode: "NEFT", reference: "", bank: "", remarks: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm((f) => ({ ...f, amount: due, date: today() })); }, [open, due]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/payments", { ...form, doc_id: doc.id, amount: Number(form.amount) });
      toast.success("Payment recorded");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Record payment</DialogTitle>
          <DialogDescription>
            {doc?.number} . Total {money(doc?.total)} . Outstanding <b className="text-foreground">{money(due)}</b>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Amount received" required>
              <Input type="number" min="1" required value={form.amount} data-testid="pay-amount"
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </Field>
            <Field label="Payment date">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </Field>
            <Field label="Payment mode">
              <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Transaction reference">
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
            </Field>
            <Field label="Bank name" className="sm:col-span-2">
              <Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} />
            </Field>
          </div>
          <Field label="Remarks">
            <Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" variant="success" disabled={busy} data-testid="pay-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <IndianRupee className="h-4 w-4" />} Save payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
